import { isNonEmptyObject } from "@cayuse/shared-react.utils.type-checks";
import { _ } from "@flowable/forms";
import { cloneDeep } from "lodash";
import { v4 as uuidv4 } from "uuid";

import { isPresent, validateIsRequired } from "components/flowable/ResearchTeam/utils";
import { getDateFormat } from "utils";

const toLocalStartOfDayTimestamp = value => {
	if (!isPresent(value)) {
		return null;
	}

	if (value instanceof Date) {
		return new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();
	}

	if (typeof value === "string") {
		const dateParts = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:T.*)?$/);
		if (dateParts) {
			const year = Number(dateParts[1]);
			const monthIndex = Number(dateParts[2]) - 1;
			const day = Number(dateParts[3]);

			return new Date(year, monthIndex, day).getTime();
		}
	}

	const parsedDate = new Date(value);

	if (Number.isNaN(parsedDate.getTime())) {
		return null;
	}

	return new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate()).getTime();
};

const monthNameToIndex = (month) => {
	if (typeof month === "number") {
		return month - 1; // Convert 1-12 to 0-11
	}
	const monthNames = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];
	const index = monthNames.indexOf(String(month).toLowerCase());
	return index >= 0 ? index : Number(month) - 1;
};

export const getRelativeDate = (operator, value, unit) => {
	const now = new Date();
	now.setHours(0, 0, 0, 0); // Normalize to the start of the day
	const multiplier = operator === "minus" ? -1 : operator === "plus" ? 1 : 0;
	switch (unit) {
	case "days":
		return new Date(now.setDate(now.getDate() + multiplier * value)).getTime();
	case "months":
		return new Date(now.setMonth(now.getMonth() + multiplier * value)).getTime();
	case "years":
		return new Date(now.setFullYear(now.getFullYear() + multiplier * value)).getTime();
	default:
		return null;
	}
};

export const getAbsoluteDate = (year, month, day) => {
	if (year && month && day) {
		const date = new Date(Number(year), monthNameToIndex(month), Number(day));
		date.setHours(0, 0, 0, 0);

		return date.getTime();
	}

	return null;
};

export const validateTableField = (value, column) => {
	const { id: type } = column || {};
	let errors = [];
	if(column?.required) {
		validateIsRequired(value, column?.required, errors);
	}

	let min = column?.["minimum-length"];
	let max = column?.["maximum-length"];
	let minError = "minLength";
	let maxError = "maxLength";
	let valueToValidate = value?.length ?? 0;
	switch (type) {
	case "number":
	case "decimal":
		minError = "minValue";
		maxError = "maxValue";
		valueToValidate = Number(value);
		break;
	case "date":
		minError = "minDate";
		maxError = "maxDate";
		min = column?.["date-validation-minimum"] === "relative" ?
			getRelativeDate(column?.["date-operator-minimum"], column?.["date-value-minimum"], column?.["date-unit-minimum"]) :
			column?.["date-validation-minimum"] === "absolute" ?
				getAbsoluteDate(column?.["year-minimum"], column?.["month-minimum"], column?.["day-minimum"]) :
				undefined;
		max = column?.["date-validation-maximum"] === "relative" ?
			getRelativeDate(column?.["date-operator-maximum"], column?.["date-value-maximum"], column?.["date-unit-maximum"]) :
			column?.["date-validation-maximum"] === "absolute" ?
				getAbsoluteDate(column?.["year-maximum"], column?.["month-maximum"], column?.["day-maximum"]) :
				undefined;
		valueToValidate = toLocalStartOfDayTimestamp(value);
		break;
	case "dropdownMultiple":
	case "orgFinder":
	case "personFinder":
	case "unitFinder":
		minError = "minItems";
		maxError = "maxItems";
		break;
	default:
		break;
	}
	if (isPresent(valueToValidate) && (min || max)) {
		if (min && valueToValidate < min) {
			errors.push(minError);
		}
		if (max && valueToValidate > max) {
			errors.push(maxError);
		}
	}

	return errors;
};

export const getDate = (config, type) => {
	let date;
	if (config?.[`date-validation-${type}`] === "relative") {
		const oprator = config?.[`date-operator-${type}`] === "minus" ? "-" : "";
		const unit = config?.[`date-unit-${type}`] === "days" ? "D" :
			config?.[`date-unit-${type}`] === "months" ? "M" :
				config?.[`date-unit-${type}`] === "years" ? "Y" : "";
		date = `P${oprator}${config?.[`date-value-${type}`]}${unit}`;
	} else if (config?.[`date-validation-${type}`] === "absolute") {
		date = new Date(Number(config?.[`year-${type}`]), monthNameToIndex(config?.[`month-${type}`]), Number(config?.[`day-${type}`]));
		date.setHours(0, 0, 0, 0);
	}

	return date;
};

export const getColumnConfig = ({ config, column, value, originalColumn, extraScope }) => {
	if (!config || !column) {
		return {};
	}

	const columnSettings = { ...column, ...column.component };
	const originalColumnSettings = { ...originalColumn, ...originalColumn.component };

	const baseConfig = {
		label: "",
		$path: `${config.$path}.${columnSettings.label}`,
		type: columnSettings.id,
		field: columnSettings.label,
		enabled: config.enabled,
		isRequired: columnSettings.required,
		value: value ?? "",
		extraSettings: {},
		$scope: config.$scope,
	};

	const $errors = validateTableField(value, columnSettings);

	if(originalColumnSettings["custom-validations"]?.length) {
		baseConfig.customValidations = originalColumnSettings["custom-validations"].map(validation => ({
			expression: _.evalExpression(validation.expression, { ...config.$scope, [config.$path]: extraScope ?? {} }),
			errorMsg: validation.errorMsg,
		}));
		if(baseConfig.customValidations.some(validation => !validation.expression)) {
			$errors.push("custom");
		}
	}
	switch (columnSettings.id) {
	case "text":
		return {
			...baseConfig,
			$errors: $errors.length ? $errors : null,
			extraSettings: {
				maxLength: columnSettings?.["maximum-length"],
				minLength: columnSettings?.["minimum-length"],
				validationPanelDisplay: "show",
			},
			minLengthErrorMessage: columnSettings?.["minimum-error-message"],
			maxLengthErrorMessage: columnSettings?.["maximum-error-message"],
		};
	case "number":
		return {
			...baseConfig,
			$errors,
			extraSettings: {
				numberFormat: columnSettings?.format,
				max: columnSettings?.["maximum-length"],
				min: columnSettings?.["minimum-length"],
				minItemsErrorMessage: columnSettings?.["minimum-error-message"],
				maxItemsErrorMessage: columnSettings?.["maximum-error-message"],
				validationPanelDisplay: "show",
				numberType: "integer",
				fractionSize: 0,
			},
			type: "one",

		};
	case "decimal":
		return {
			...baseConfig,
			$errors,
			extraSettings: {
				numberFormat: columnSettings?.format,
				fractionSize: columnSettings?.["table-fraction-size"],
				max: columnSettings?.["maximum-length"],
				min: columnSettings?.["minimum-length"],
				minItemsErrorMessage: columnSettings?.["minimum-error-message"],
				maxItemsErrorMessage: columnSettings?.["maximum-error-message"],
				validationPanelDisplay: "show",
				numberType: "float",
			},
			type: "one",
		};
	case "date":
		const dateConfig = {
			...baseConfig,
			$errors,
			extraSettings: {
				minDate: getDate(columnSettings, "minimum"),
				maxDate: getDate(columnSettings, "maximum"),
				minDateErrorMessage: columnSettings?.["minimum-error-message"],
				maxDateErrorMessage: columnSettings?.["maximum-error-message"],
				enableTime: false,
				storeWithoutTime: true,
				validationPanelDisplay: "show",
			}
		};
		dateConfig.extraSettings = getDateFormat(dateConfig.extraSettings);

		return dateConfig;
	case "radio":
		return {
			...baseConfig,
			$errors,
			extraSettings: {
				items: columnSettings?.["table-items"]?.map(item => ({ text: item.label, value: item.label })) ?? [],
			},
		};
	case "checkbox":
		return {
			...baseConfig,
			$errors,
			type: "boolean",
		};
	case "dropdownSingle":
		return {
			...baseConfig,
			type: "select",
			$errors,
			extraSettings: {
				items: columnSettings?.["table-items"]?.map(item => ({ text: item.label, value: item.label })) ?? [],
				multi: false,
				validationPanelDisplay: "show",
			},
		};
	case "dropdownMultiple":
		return {
			...baseConfig,
			$errors,
			type: "select",
			extraSettings: {
				items: columnSettings?.["table-items"]?.map(item => ({ text: item.label, value: item.label })) ?? [],
				multi: true,
				minItems: columnSettings?.["minimum-length"],
				maxItems: columnSettings?.["maximum-length"],
				minItemsErrorMessage: columnSettings?.["minimum-error-message"],
				maxItemsErrorMessage: columnSettings?.["maximum-error-message"],
				validationPanelDisplay: "show",
			},
		};
	case "expressionField":
		return {
			...baseConfig,
			enabled: false,
			expression: originalColumnSettings.expression,
			extraSettings: {
				decimalValue: columnSettings?.["table-decimal-value"],
				numberFormat: columnSettings?.["expression-format"],
				fractionSize: columnSettings?.["expression-fraction-size"],
			},
			$original: {
				expression: originalColumnSettings.expression,
			},
		};
	case "orgFinder":
	case "personFinder":
	case "unitFinder":
		let extraSettings = {
			validationPanelDisplay: "show",
			multi: columnSettings?.["multi-select"]
		};
		if(columnSettings?.["multi-select"]) {
			extraSettings = {
				...extraSettings,
				minItems: columnSettings?.["minimum-items"],
				maxItems: columnSettings?.["maximum-items"],
				minItemsErrorMessage: columnSettings?.["minimum-items-error-message"],
				maxItemsErrorMessage: columnSettings?.["maximum-items-error-message"],
			};
		}

		return {
			...baseConfig,
			$errors,
			extraSettings,
		};

	default:
		return baseConfig;
	}
};

export const preparePayload = (rows, columns) => {
	if(!rows) {
		return undefined;
	}

	const payload = rows.map(row => {
		const { key, ...rest } = row;

		return rest;
	});

	if(payload.length === 1 && !isNonEmptyObject(payload[0])) {
		return undefined;
	}	else {
		return {
			type: "tableObject",
			rows: payload.map((row, index) => ({
				rowNumber: index,
				columns: Object.entries(row).map(([ label, value ]) => ({
					name: label,
					type: columns.find(column => column.label === label)?.id,
					value,
				})),
			})),
		};
	}
};

export const parseResponse = configValue => {
	const rows = isNonEmptyObject(configValue) ? cloneDeep(configValue.rows) : [];

	return rows.map(row => ({
		...row.columns.reduce((acc, column) => ({ ...acc, [column.name]: column.value }), {}),
		key: uuidv4(),
	}));
};